import { useCallback, useEffect, useRef } from 'react';

/**
 * Agenda chamadas com atraso mantendo uma fila por chave.
 *
 * Um debounce único e compartilhado descarta as chamadas anteriores: marcar
 * três itens em menos de meio segundo salvaria só o último. Aqui cada chave
 * (id do item) tem o seu próprio temporizador, então edições simultâneas em
 * itens diferentes nunca se atropelam.
 */
const useKeyedDebounce = (callback, delay = 500) => {
  const timers = useRef(new Map());
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const cancel = useCallback((key) => {
    const timer = timers.current.get(key);

    if (timer) {
      clearTimeout(timer);
      timers.current.delete(key);
    }
  }, []);

  const schedule = useCallback(
    (key, ...args) => {
      cancel(key);
      const timer = setTimeout(() => {
        timers.current.delete(key);
        callbackRef.current(...args);
      }, delay);
      timers.current.set(key, timer);
    },
    [cancel, delay]
  );

  /** Executa agora, descartando qualquer agendamento pendente da mesma chave. */
  const run = useCallback(
    (key, ...args) => {
      cancel(key);
      callbackRef.current(...args);
    },
    [cancel]
  );

  const isPending = useCallback((key) => timers.current.has(key), []);

  useEffect(() => {
    const pending = timers.current;
    return () => {
      pending.forEach((timer) => clearTimeout(timer));
      pending.clear();
    };
  }, []);

  return { schedule, run, cancel, isPending };
};

export default useKeyedDebounce;
